<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Houses"
    :items="allHouses || []"
    :headers="headers"
    item-key="House_SK"
    selection-state-name="selectedHouseSks"
    :loading="allHousesLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="House"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.House_Name" label="House Name" autofocus></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                :items="allCompanies || []"
                item-title="Company_Name"
                item-value="Company_SK"
                v-model="editedItem.Company_FK"
                label="Company"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Houses'"
    item-key="House_SK"
    display-field="House_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="House"
    :item-name="itemToDelete?.House_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useQuery, useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ALL_HOUSES from '@/graphql/House/allHouses.gql'
import ADD_HOUSE from '@/graphql/House/addHouse.gql'
import UPDATE_HOUSE from '@/graphql/House/updateHouse.gql'
import REMOVE_HOUSE from '@/graphql/House/removeHouse.gql'
import MERGE_HOUSE from '@/graphql/House/mergeHouse.gql'
import ALL_COMPANIES from '@/graphql/Company/allCompanies.gql'

const store = useStore()

const {
  result: allHousesResult,
  loading: allHousesLoading,
} = useQuery(ALL_HOUSES)
const allHouses = computed(() => allHousesResult.value?.allHouses)

const {
  result: allCompaniesResult,
} = useQuery(ALL_COMPANIES)
const allCompanies = computed(() => allCompaniesResult.value?.allCompanies)

const headers = [
  { title: '#', key: 'House_SK', filterType: 'number', align: 'center' },
  { title: 'Company', key: 'Company.Company_Name', filterType: 'combo', align: 'left' },
  { title: 'Name', key: 'House_Name', filterType: 'text', align: 'left' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New House' : 'Edit House')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allHouses.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addHouse } = useMutation(ADD_HOUSE,
  () => ({
    update: (cache, { data: { addHouse } }) => {
      const { allHouses } = cache.readQuery({ query: ALL_HOUSES })
      const newData = { allHouses: [...allHouses, addHouse] }
      cache.writeQuery({ query: ALL_HOUSES, data: newData })
    }
  })
)

const { mutate: updateHouse } = useMutation(UPDATE_HOUSE)

const save = () => {
  const input = {
    House_SK: editedItem.value.House_SK,
    House_Name: editedItem.value.House_Name,
    Company_FK: editedItem.value.Company_FK
  }
  if (editedIndex.value > -1) {
    updateHouse({ UpdateHouseInput: input })
      .then(() => close())
      .catch((error) => addError(error.message))
  } else {
    const { House_SK: _House_SK, ...addInput } = input
    addHouse({ AddHouseInput: addInput })
      .then(() => close())
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeHouse } = useMutation(REMOVE_HOUSE)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeHouse({
    House_SK: item.House_SK
  }, {
    update: (cache, { data: { removeHouse } }) => {
      const { allHouses } = cache.readQuery({ query: ALL_HOUSES })
      const newData = { allHouses: allHouses.filter((h) => h.House_SK !== removeHouse) }
      cache.writeQuery({ query: ALL_HOUSES, data: newData })
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.House_SK)
    }
  })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeHouse } = useMutation(MERGE_HOUSE)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeHouse({
    MergeHouseInput: {
      survivor_House_SK: mergeData.survivor,
      duplicate_House_SKs: mergeData.duplicates
    }
  }, {
    update: (cache) => {
      const { allHouses } = cache.readQuery({ query: ALL_HOUSES })
      const newData = {
        allHouses: allHouses.filter(house => !mergeData.duplicates.includes(house.House_SK))
      }
      cache.writeQuery({ query: ALL_HOUSES, data: newData })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} houses`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
